import React from 'react';
import { useIntl } from 'react-intl';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faMagnifyingGlass } from '@fortawesome/pro-light-svg-icons/faMagnifyingGlass';
import { TypedMemo } from '../../../common/types';
import { Panel, PanelBody, PanelHead } from '../../components/primitives/Panel';
import { SMLButton } from '../../components/primitives/SMLButton';
import { browseStateMessages as messages } from '../browseMessages';
import { NoResultWish } from '../../wishBox/NoResultWish';
/* eslint-disable css-modules/no-unused-class -- a shared stylesheet; each part uses its own slice of it */
import styles from './BrowseStates.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface BrowseNoResultsProps {
  query: string;
  /** What the person looked for: the type tab of the search. */
  kind?: 'artist' | 'track' | 'playlist' | 'all';
  didYouMean: string | null;
  filterOn: boolean;
  onSearch: (query: string) => void;
  onClearFilters: () => void;
  onPasteLink: () => void;
}

/** The "paste a playlist link" button inside the hint. A factory outside the component, so React keeps one element type. */
function pasteLinkRenderer(onClick: () => void) {
  return function PasteLink(chunks: React.ReactNode[]) {
    return (
      <button key="paste" type="button" className={styles.inlineLink} onClick={onClick}>
        {chunks}
      </button>
    );
  };
}

/**
 * Design b2: no results. "Search <name>" shows only when the server sends a closer name; "Clear the
 * filter" only when a filter is on.
 */
const BrowseNoResultsComponent: React.FC<BrowseNoResultsProps> = ({
  query,
  kind = 'all',
  didYouMean,
  filterOn,
  onSearch,
  onClearFilters,
  onPasteLink,
}) => {
  const intl = useIntl();
  const body = intl.formatMessage(messages.noResultsBody);
  return (
    <Panel as="section">
      <PanelHead title={intl.formatMessage(messages.resultsTitle)} right="0" />
      <PanelBody>
        <div className={styles.emptyBody}>
          <span className={styles.emptyMark} aria-hidden="true">
            <FontAwesomeIcon icon={faMagnifyingGlass} />
          </span>
          <h3 className={styles.emptyTitle}>{intl.formatMessage(messages.noResultsTitle, { query })}</h3>
          <p className={styles.emptyText}>
            {filterOn ? `${body} ${intl.formatMessage(messages.noResultsFilterOn)}` : body}
          </p>
          <div className={styles.actions}>
            {didYouMean !== null && (
              <SMLButton variant="primary" onClick={() => onSearch(didYouMean)}>
                {intl.formatMessage(messages.searchInstead, { name: didYouMean })}
              </SMLButton>
            )}
            {filterOn && <SMLButton onClick={onClearFilters}>{intl.formatMessage(messages.clearFilter)}</SMLButton>}
          </div>
          <p className={styles.emptyText}>
            {intl.formatMessage(messages.pasteHint, {
              link: pasteLinkRenderer(onPasteLink),
            })}
          </p>
          <NoResultWish surface="browse" kind={kind} query={query} />
        </div>
      </PanelBody>
    </Panel>
  );
};

export const BrowseNoResults = TypedMemo(BrowseNoResultsComponent);
